import { useState, useEffect, useCallback } from 'react'
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AppFactoryDeviceTypesProvider, useAppFactoryDeviceTypes } from '../contexts/AppFactoryDeviceTypesContext'
import { listTemplates, getFramerPresetOverrides, listComponentStyleNameOverrides, type Template } from '../services/appfactoryApi'
import { listLoginStyles } from '../pages/appfactory/loginStyleRegistry'
import { listTraitStyles } from './traitStyles'
import { listStateStyles } from './traitStyles/stateStyleRegistry'
import { loadGlobalNormalization, applyMergeRules } from '../services/deviceTypeNormalizationStore'
import './AppFactoryLayout.css'

function DeviceTypesList() {
  const { registryEntries, selectedType, getIconSlot1, loading } = useAppFactoryDeviceTypes()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const typeParam = searchParams.get('type')?.trim() || ''
  const isDeviceTypeRoute =
    pathname === '/appfactory/device-type-templates' ||
    pathname.startsWith('/appfactory/device-type-templates')
  if (loading || registryEntries.length === 0) return null
  return (
    <div className="appfactory-device-types-list">
      {registryEntries.map((entry) => {
        const { dataUrl, materialIcon } = getIconSlot1(entry.deviceType)
        const to = `/appfactory/device-type-templates?type=${encodeURIComponent(entry.deviceType)}`
        const isActive = isDeviceTypeRoute && (typeParam || selectedType) === entry.deviceType
        return (
          <Link
            key={entry.deviceType}
            to={to}
            className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
          >
            {dataUrl ? (
              <img src={dataUrl} alt="" className="appfactory-device-type-icon-img" />
            ) : (
              <span className="material-symbols-rounded">{materialIcon}</span>
            )}
            <span>{entry.deviceType.toUpperCase()}</span>
          </Link>
        )
      })}
    </div>
  )
}

function TemplatesList() {
  const { pathname } = useLocation()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const fetchTemplates = useCallback(() => {
    setLoading(true)
    listTemplates()
      .then((list) => setTemplates(list))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])
  useEffect(() => {
    const onRefresh = () => fetchTemplates()
    window.addEventListener('appfactory-templates-refresh', onRefresh)
    return () => window.removeEventListener('appfactory-templates-refresh', onRefresh)
  }, [fetchTemplates])
  if (loading || templates.length === 0) return null
  return (
    <div className="appfactory-device-types-list">
      {templates.map((t) => (
        <Link
          key={t.id}
          to={`/appfactory/templates/${t.id}`}
          className={`appfactory-device-type-item${pathname === `/appfactory/templates/${t.id}` ? ' active' : ''}`}
        >
          <span className="material-symbols-rounded">view_quilt</span>
          <span>{t.name}</span>
        </Link>
      ))}
    </div>
  )
}

function ComponentStylesList() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { registryEntryMap } = useAppFactoryDeviceTypes()
  const [mergedStates, setMergedStates] = useState<string[]>([])
  const [normLoaded, setNormLoaded] = useState(false)
  const [traitsExpanded, setTraitsExpanded] = useState(false)
  const [statesExpanded, setStatesExpanded] = useState(false)
  const [traitStylesExpanded, setTraitStylesExpanded] = useState(false)
  const [dataChartsExpanded, setDataChartsExpanded] = useState(false)
  const [themesExpanded, setThemesExpanded] = useState(false)
  const [framerExpanded, setFramerExpanded] = useState(false)
  const [loginPageExpanded, setLoginPageExpanded] = useState(false)
  const [framerPresetOptions, setFramerPresetOptions] = useState<{ value: string; label: string }[]>([
    { value: 'default', label: 'Default' },
    { value: 'Glass', label: 'Glass' },
    { value: 'GlassV2', label: 'GlassV2' },
    { value: 'Solid', label: 'Solid' },
    { value: 'Minimal', label: 'Minimal' },
  ])

  const fetchFramerPresets = useCallback(() => {
    Promise.all([getFramerPresetOverrides(), listComponentStyleNameOverrides()])
      .then(([overrides, nameOverrides]) => {
        const builtin = [
          { value: 'default', label: 'Default' },
          { value: 'Glass', label: 'Glass' },
          { value: 'GlassV2', label: 'GlassV2' },
          { value: 'Solid', label: 'Solid' },
          { value: 'Minimal', label: 'Minimal' },
        ]
        const customIds = Object.keys(overrides).filter((k) => !builtin.some((b) => b.value === k))
        const custom = customIds.map((id) => ({
          value: id,
          label: nameOverrides[`framer-${id}`] || id,
        }))
        setFramerPresetOptions([...builtin, ...custom])
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchFramerPresets()
  }, [fetchFramerPresets])

  useEffect(() => {
    const onRefresh = () => fetchFramerPresets()
    window.addEventListener('appfactory-framer-presets-refresh', onRefresh)
    return () => window.removeEventListener('appfactory-framer-presets-refresh', onRefresh)
  }, [fetchFramerPresets])

  const DATA_CHARTS = [
    { id: 'TemperatureChart', label: 'TemperatureChart', icon: 'thermostat' },
    { id: 'EnergyChart', label: 'EnergyChart', icon: 'electric_bolt' },
    { id: 'AirQualityChart', label: 'AirQualityChart', icon: 'air' },
  ] as const

  const traits = (() => {
    const set = new Set<string>()
    for (const entry of Object.values(registryEntryMap)) {
      for (const t of entry.traits || []) {
        if (t?.trim()) set.add(t.trim())
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  })()

  useEffect(() => {
    let cancelled = false
    loadGlobalNormalization()
      .then((norm) => {
        if (cancelled) return
        const rawStates = new Set<string>()
        for (const entry of Object.values(registryEntryMap)) {
          for (const s of entry.states || []) {
            if (s?.trim()) rawStates.add(s.trim())
          }
        }
        const merged = applyMergeRules(Array.from(rawStates), norm.stateRules || [])
        setMergedStates(merged)
        setNormLoaded(true)
      })
      .catch(() => setNormLoaded(true))
    return () => {
      cancelled = true
    }
  }, [registryEntryMap])

  const traitStyles = listTraitStyles()
  const stateStyles = listStateStyles(mergedStates)
  const allStyles = [...traitStyles, ...stateStyles]
  const hasTraits = traits.length > 0
  const hasStates = mergedStates.length > 0
  const hasTraitStyles = traitStyles.length > 0
  const hasStateStyles = stateStyles.length > 0
  if (!hasTraits && !hasStates && !hasTraitStyles && !hasStateStyles) return null

  return (
    <div className="appfactory-component-styles-list">
      {hasTraits && (
        <div className="appfactory-component-styles-subsection">
          <button
            type="button"
            className="appfactory-component-styles-subtitle-toggle"
            onClick={() => setTraitsExpanded((prev) => !prev)}
            aria-label={traitsExpanded ? '收合 traits' : '展開 traits'}
          >
            <span className="appfactory-component-styles-subtitle">traits</span>
            <span className="material-symbols-rounded">
              {traitsExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {traitsExpanded && (
            <div className="appfactory-device-types-list">
              {traits.map((t) => {
                const filterTrait = searchParams.get('trait')
                const isActive = pathname === '/appfactory/components' && filterTrait === t
                return (
                  <Link
                    key={t}
                    to={`/appfactory/components?filter=traits&trait=${encodeURIComponent(t)}`}
                    className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                  >
                    <span className="material-symbols-rounded">tune</span>
                    <span>{t}</span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
      {hasStates && normLoaded && (
        <div className="appfactory-component-styles-subsection">
          <button
            type="button"
            className="appfactory-component-styles-subtitle-toggle"
            onClick={() => setStatesExpanded((prev) => !prev)}
            aria-label={statesExpanded ? '收合 states' : '展開 states'}
          >
            <span className="appfactory-component-styles-subtitle">states</span>
            <span className="material-symbols-rounded">
              {statesExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {statesExpanded && (
            <div className="appfactory-device-types-list">
              {mergedStates.map((s) => {
                const filterState = searchParams.get('state')
                const isActive = pathname === '/appfactory/components' && filterState === s
                return (
                  <Link
                    key={s}
                    to={`/appfactory/components?filter=states&state=${encodeURIComponent(s)}`}
                    className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                  >
                    <span className="material-symbols-rounded">analytics</span>
                    <span>{s}</span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
      <div className="appfactory-component-styles-subsection">
        <button
          type="button"
          className="appfactory-component-styles-subtitle-toggle"
          onClick={() => setDataChartsExpanded((prev) => !prev)}
          aria-label={dataChartsExpanded ? '收合 DataCharts' : '展開 DataCharts'}
        >
          <span className="appfactory-component-styles-subtitle">DataCharts</span>
          <span className="material-symbols-rounded">
            {dataChartsExpanded ? 'expand_less' : 'expand_more'}
          </span>
        </button>
        {dataChartsExpanded && (
          <div className="appfactory-device-types-list">
            {DATA_CHARTS.map((chart) => {
              const to = `/appfactory/components?filter=datacharts&chart=${encodeURIComponent(chart.id)}`
              const isActive =
                pathname === '/appfactory/components' &&
                searchParams.get('filter') === 'datacharts' &&
                searchParams.get('chart') === chart.id
              return (
                <Link
                  key={chart.id}
                  to={to}
                  className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                >
                  <span className="material-symbols-rounded">{chart.icon}</span>
                  <span>{chart.label}</span>
                </Link>
              )
            })}
          </div>
        )}
      </div>
      {hasTraitStyles && (
        <div className="appfactory-component-styles-subsection">
          <button
            type="button"
            className="appfactory-component-styles-subtitle-toggle"
            onClick={() => setTraitStylesExpanded((prev) => !prev)}
            aria-label={traitStylesExpanded ? '收合 traits and states' : '展開 traits and states'}
          >
            <span className="appfactory-component-styles-subtitle">traits & states</span>
            <span className="material-symbols-rounded">
              {traitStylesExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {traitStylesExpanded && (
            <div className="appfactory-device-types-list">
              {traitStyles.map((s) => {
                const to = `/appfactory/components/${encodeURIComponent(s.trait)}/${encodeURIComponent(s.styleId)}`
                return (
                  <Link
                    key={`trait-${s.trait}-${s.styleId}`}
                    to={to}
                    className={`appfactory-device-type-item${pathname === to ? ' active' : ''}`}
                  >
                    <span className="material-symbols-rounded">palette</span>
                    <span>{s.name}</span>
                  </Link>
                )
              })}
              {stateStyles.map((s) => {
                const to = `/appfactory/components?filter=states&state=${encodeURIComponent(s.stateKey)}`
                const isActive = pathname === '/appfactory/components' && searchParams.get('filter') === 'states' && searchParams.get('state') === s.stateKey
                return (
                  <Link
                    key={`state-${s.stateKey}`}
                    to={to}
                    className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                  >
                    <span className="material-symbols-rounded">analytics</span>
                    <span>{s.name}</span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
      <div className="appfactory-component-styles-subsection">
        <button
          type="button"
          className="appfactory-component-styles-subtitle-toggle"
          onClick={() => setThemesExpanded((prev) => !prev)}
          aria-label={themesExpanded ? '收合 Themes' : '展開 Themes'}
        >
          <span className="appfactory-component-styles-subtitle">Themes</span>
          <span className="material-symbols-rounded">
            {themesExpanded ? 'expand_less' : 'expand_more'}
          </span>
        </button>
        {themesExpanded && (
          <div className="appfactory-component-styles-subsection">
            <button
              type="button"
              className="appfactory-component-styles-subtitle-toggle"
              onClick={() => setFramerExpanded((prev) => !prev)}
              aria-label={framerExpanded ? '收合 Framer' : '展開 Framer'}
            >
              <span className="appfactory-component-styles-subtitle">Framer</span>
              <span className="material-symbols-rounded">
                {framerExpanded ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {framerExpanded && (
              <div className="appfactory-device-types-list">
                {framerPresetOptions.map((styleOpt) => {
                  const isActive =
                    pathname.includes('/appfactory/components') &&
                    searchParams.get('filter') === 'themes' &&
                    searchParams.get('theme') === 'framer' &&
                    searchParams.get('style') === styleOpt.value
                  return (
                    <button
                      key={styleOpt.value}
                      type="button"
                      className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                      onClick={() => {
                        navigate(
                          `/appfactory/components?filter=themes&theme=framer&style=${encodeURIComponent(styleOpt.value)}`,
                          { replace: false }
                        )
                      }}
                    >
                      <span className="material-symbols-rounded">palette</span>
                      <span>{styleOpt.label}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="appfactory-component-styles-subsection">
              <button
                type="button"
                className="appfactory-component-styles-subtitle-toggle"
                onClick={() => setLoginPageExpanded((prev) => !prev)}
                aria-label={loginPageExpanded ? '收合 Login Page' : '展開 Login Page'}
              >
                <span className="appfactory-component-styles-subtitle">Login Page</span>
                <span className="material-symbols-rounded">
                  {loginPageExpanded ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {loginPageExpanded && (
                <div className="appfactory-device-types-list">
                  {listLoginStyles().map((styleOpt) => {
                    const isActive =
                      pathname.includes('/appfactory/components') &&
                      searchParams.get('filter') === 'themes' &&
                      searchParams.get('theme') === 'login-page' &&
                      searchParams.get('style') === styleOpt.styleId
                    return (
                      <button
                        key={styleOpt.styleId}
                        type="button"
                        className={`appfactory-device-type-item${isActive ? ' active' : ''}`}
                        onClick={() => {
                          navigate(
                            `/appfactory/components?filter=themes&theme=login-page&style=${encodeURIComponent(styleOpt.styleId)}`,
                            { replace: false }
                          )
                        }}
                      >
                        <span className="material-symbols-rounded">login</span>
                        <span>{styleOpt.label}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export type NavSubItem = { to: string; label: string; icon?: string }
export type NavItem = { to: string; label: string; icon: string; hasSubItems: boolean; subItems?: NavSubItem[] }

interface AppFactoryLayoutProps {
  title?: string
  brandTitle?: string
  basePath?: string
  customNavItems?: NavItem[]
  subtitle?: string
  showBackButton?: boolean
  loading?: boolean
  /** When true, main area uses gradient background (dashboard style) */
  useGradient?: boolean
  /** Optional content rendered at the bottom of the sidebar */
  sidebarBottom?: React.ReactNode
}

export default function AppFactoryLayout({
  title = 'AppFactory',
  brandTitle,
  basePath = '/appfactory',
  customNavItems,
  subtitle = 'Template & Component Styles 發行後台',
  showBackButton = true,
  loading = false,
  useGradient,
  sidebarBottom,
}: AppFactoryLayoutProps) {
  const resolvedBrand = brandTitle ?? title
  const bp = basePath.replace(/\/+$/, '')
  const location = useLocation()
  const path = location.pathname
  const isOverview = path === bp || path === bp + '/'
  const effectiveGradient = useGradient ?? isOverview

  const routeTitle = (() => {
    if (isOverview) return resolvedBrand
    if (path.includes('/templates/')) return 'Template'
    if (path.endsWith('/templates')) return 'Templates'
    if (path.includes('/components/')) return 'Component Style'
    if (path.endsWith('/components')) return 'Component Styles'
    if (path.endsWith('/deploy')) return 'Deploy'
    if (path.endsWith('/appsuite-iam')) return 'App Suite IAM'
    if (path.endsWith('/device-type-templates')) return 'Device Modal Templates'
    return title
  })()

  const defaultNavItems: NavItem[] = [
    { to: bp, label: 'Overview', icon: 'dashboard', hasSubItems: false },
    { to: `${bp}/appsuite-iam`, label: 'App Suite IAM', icon: 'admin_panel_settings', hasSubItems: false },
    { to: `${bp}/deploy`, label: 'Deploy', icon: 'rocket_launch', hasSubItems: false },
    { to: `${bp}/templates`, label: 'App Templates', icon: 'view_quilt', hasSubItems: true },
    { to: `${bp}/device-type-templates`, label: 'Device Modal Templates', icon: 'devices', hasSubItems: true },
  ]
  const navItems = customNavItems ?? defaultNavItems

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set())
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const isExpanded = (itemTo: string) => expandedPaths.has(itemTo)

  useEffect(() => {
    for (const item of navItems) {
      if (!item.hasSubItems) continue
      const matchesSelf = path === item.to || path.startsWith(item.to + '/')
      const matchesSub = item.subItems?.some((sub) => path === sub.to || path.startsWith(sub.to + '/'))
      if (matchesSelf || matchesSub) {
        setExpandedPaths((prev) => { const next = new Set(prev); next.add(item.to); return next })
      }
    }
  }, [path])

  const navigate = useNavigate()
  const handleToggle = (item: NavItem) => {
    const wasExpanded = isExpanded(item.to)
    if (!wasExpanded && !item.subItems && path !== item.to && !path.startsWith(item.to + '/')) {
      navigate(item.to)
    }
    setExpandedPaths((prev) => {
      const next = new Set(prev)
      if (next.has(item.to)) next.delete(item.to)
      else next.add(item.to)
      return next
    })
  }

  return (
    <AppFactoryDeviceTypesProvider>
        <div className="appfactory-layout">
          <aside className={`appfactory-sidebar${sidebarCollapsed ? ' appfactory-sidebar-collapsed' : ''}`}>
            <div className="appfactory-sidebar-header">
              <Link to="/" className="appfactory-sidebar-home" title="Home">
                <img src="/home-icon.svg" alt="Home" />
              </Link>
              {!sidebarCollapsed && (
                <>
                  <span className="material-symbols-rounded">precision_manufacturing</span>
                  <span className="appfactory-sidebar-title">{resolvedBrand}</span>
                  <button
                    type="button"
                    className="appfactory-sidebar-collapse-btn"
                    onClick={() => setSidebarCollapsed(true)}
                    aria-label="收合側邊欄"
                    title="收合側邊欄"
                  >
                    <span className="material-symbols-rounded">chevron_left</span>
                  </button>
                </>
              )}
            </div>
            <div className="appfactory-sidebar-separator" />
            {sidebarCollapsed ? (
              <button
                type="button"
                className="appfactory-sidebar-expand-btn"
                onClick={() => setSidebarCollapsed(false)}
                aria-label="展開側邊欄"
                title="展開側邊欄"
              >
                <span className="material-symbols-rounded">chevron_right</span>
              </button>
            ) : (
            <div className="appfactory-nav-section">
              {navItems.map((item) => (
                <div key={item.to}>
                  {item.hasSubItems ? (
                    <div className="appfactory-nav-item-wrapper">
                      <button
                        type="button"
                        className={`appfactory-nav-item appfactory-nav-item-toggle${path === item.to || (item.to !== bp && path.startsWith(item.to)) || item.subItems?.some((s) => path === s.to || path.startsWith(s.to + '/')) ? ' active' : ''}`}
                        onClick={() => handleToggle(item)}
                        aria-label={isExpanded(item.to) ? '收合' : '展開'}
                      >
                        <span className="material-symbols-rounded">{item.icon}</span>
                        <span>{item.label}</span>
                        <span className="material-symbols-rounded appfactory-nav-chevron">
                          {isExpanded(item.to) ? 'expand_less' : 'expand_more'}
                        </span>
                      </button>
                      {isExpanded(item.to) && (
                        item.subItems ? (
                          <div className="appfactory-device-types-list">
                            {item.subItems.map((sub) => (
                              <Link
                                key={sub.to}
                                to={sub.to}
                                className={`appfactory-device-type-item${path === sub.to || path.startsWith(sub.to + '/') ? ' active' : ''}`}
                              >
                                {sub.icon && <span className="material-symbols-rounded" style={{ fontSize: 18 }}>{sub.icon}</span>}
                                <span>{sub.label}</span>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <>
                            {item.to.endsWith('/device-type-templates') && <DeviceTypesList />}
                            {item.to.endsWith('/templates') && <TemplatesList />}
                            {item.to.endsWith('/components') && <ComponentStylesList />}
                          </>
                        )
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.to}
                      className={`appfactory-nav-item${path === item.to || (item.to !== bp && path.startsWith(item.to)) ? ' active' : ''}`}
                    >
                      <span className="material-symbols-rounded">{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
            )}
            {sidebarBottom && !sidebarCollapsed ? (
              <div className="appfactory-sidebar-bottom">
                {sidebarBottom}
              </div>
            ) : null}
        </aside>
        <main className={`appfactory-main${effectiveGradient ? ' appfactory-main-gradient' : ''}`}>
          <Outlet />
        </main>
      </div>
    </AppFactoryDeviceTypesProvider>
  )
}
